import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDebounce } from "../../../hooks/useDebounce";
import { usePermissionOptions } from "../../../hooks/usePermissionOptions";

const ACTION_OPTIONS = [
  { value: "", label: "All" },
  { value: "create_role", label: "Create Role" },
  { value: "delete_role", label: "Delete Role" },
  { value: "update_role", label: "Update Role" },
  { value: "create_permission", label: "Create Permission" },
  { value: "update_permission", label: "Update Permission" },
  { value: "delete_permission", label: "Delete Permission" },
  { value: "assign_permission_to_role", label: "Assign Permission to Role" },
  { value: "remove_permission_from_role", label: "Remove Permission from Role" },
  { value: "assign_role", label: "Assign Role" },
  { value: "revoke_role", label: "Revoke Role" },
  { value: "update_role_scope", label: "Update Role Scope" },
];

interface AuditLogsFiltersProps {
  actionValue: string;
  onActionChange?: (value: string) => void;
  permissionValue: string;
  onPermissionChange?: (value: string) => void;
  startDate: Date | null;
  endDate: Date | null;
  onDateRangeChange?: (range: [Date | null, Date | null]) => void;
  onClearFilters?: () => void;
}

const AuditLogsFilters: React.FC<AuditLogsFiltersProps> = ({
  actionValue,
  onActionChange,
  permissionValue,
  onPermissionChange,
  startDate,
  endDate,
  onDateRangeChange,
  onClearFilters,
}) => {

  const [permissionSearch, setPermissionSearch] = useState("");
  const debouncedPermissionSearch = useDebounce(permissionSearch, 600);
  const { permissionOptions, loading: permissionsLoading } =
    usePermissionOptions(debouncedPermissionSearch);

  return (
    <div className="audit-logs-filters-row">
      <div className="audit-logs-filter-by">
        <span className="audit-logs-filter-by-label">Filter By</span>
      </div>
      <div className="audit-logs-filters-group">
        <div className="audit-logs-filter-field audit-logs-date-range-field">
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
            Date Range
          </Form.Label>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) =>
              onDateRangeChange?.(
                (update as [Date | null, Date | null]) || [null, null]
              )
            }
            placeholderText="Select date range"
            dateFormat="dd/MM/yyyy"
            isClearable
            popperPlacement="top-start"
            popperClassName="audit-logs-datepicker-popper"
            className="form-control form-control-sm audit-logs-date-range-picker"
          />
        </div>
        <div
          className="audit-logs-filter-field"
          style={{ minWidth: 220 }}
        >
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
            Action
          </Form.Label>
          <Select
            classNamePrefix="react-select"
            options={ACTION_OPTIONS}
            value={
              ACTION_OPTIONS.find((opt) => opt.value === actionValue) ??
              ACTION_OPTIONS[0]
            }
            onChange={(option) => onActionChange?.(option?.value ?? "")}
            isClearable={false}
            menuPortalTarget={document.body}
          />
        </div>
        <div
          className="audit-logs-filter-field"
          style={{ minWidth: 220 }}
        >
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
            Permission
          </Form.Label>
          <Select
            classNamePrefix="react-select"
            options={permissionOptions}
            value={
              permissionValue
                ? permissionOptions.find((opt) => opt.value === permissionValue) ??
                  null
                : null
            }
            onChange={(option) => onPermissionChange?.(option?.value ?? "")}
            isClearable
            isLoading={permissionsLoading}
            onInputChange={(input, meta) => {
              if (meta.action === "input-change") {
                setPermissionSearch(input);
              }
            }}
            filterOption={null}
            placeholder="Select permission"
            menuPortalTarget={document.body}
          />
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          className="audit-logs-clear-btn"
          onClick={onClearFilters}
        >
          Clear filters
        </Button>
      </div>
    </div>
  );
};

export default AuditLogsFilters;
